import React from 'react';
import { Body5, Body7 } from '@ui';
import { companyLabel, formatKnowableAt, formatScore, formatScoreDelta, scoreDeltaDirection } from './format';
import { MovementCell } from './MovementCell';
import { movementEntry } from './scoreSeries';
import { WashCell } from './valueWash';
import { DeltaCell, FrozenCell, NumericCell, ScoreCell, scoreTint } from './cellStyles';

// How each core column draws a row.
//
// Kept as a keyed map rather than collapsed into one renderer because these genuinely differ: a
// frozen identity, a maybe-washed number, a coloured delta, a sparkline, a timestamp. What they no
// longer do is sit hardcoded in the table's render body in an order that had nothing holding it to
// the order tableGrid lays the tracks out in.
//
// Every renderer takes `(row, ctx)`. `ctx` is built once per table render and holds the things a
// cell cannot derive from its own row — the wash scales, which depend on the whole response; the
// movement index; the tooltip builders, which know the read's knowable floor.
export const CORE_CELLS = {
  company: (row) => (
    <FrozenCell>
      <Body5>{companyLabel(row)}</Body5>
    </FrozenCell>
  ),

  // Washed only on a feed that composes nothing — see the note on washesScore for why a combined
  // score must not share the member scale.
  score: (row, { washesScore, scoreTintScale }) =>
    washesScore ? (
      <ScoreCell>
        <WashCell data-testid="washed-score" {...scoreTint(row.score, scoreTintScale)}>
          <Body5>{formatScore(row.score)}</Body5>
        </WashCell>
      </ScoreCell>
    ) : (
      <NumericCell>
        <Body5>{formatScore(row.score)}</Body5>
      </NumericCell>
    ),

  // Derived from the rows of this response, so it wears the same wash as Score, on the same scale —
  // every value here IS another row's score, so widening the scale to take them in would re-tint the
  // Score column for nothing. The title is the only place the three reasons a cell is blank are
  // separable.
  prevScore: (row, { washesScore, scoreTintScale, prevScoreTitleFor }) =>
    washesScore ? (
      <ScoreCell title={prevScoreTitleFor(row)}>
        <WashCell data-testid="washed-prev-score" {...scoreTint(row.prevScore, scoreTintScale)}>
          <Body5>{formatScore(row.prevScore)}</Body5>
        </WashCell>
      </ScoreCell>
    ) : (
      <NumericCell title={prevScoreTitleFor(row)}>
        <Body5>{formatScore(row.prevScore)}</Body5>
      </NumericCell>
    ),

  // Never washed — see DeltaCell. The wash means LEVEL on the two columns beside it; here it would
  // mean DIRECTION, and one colour asserting two things in adjacent columns is worse than none.
  scoreDelta: (row, { deltaColor, deltaTitleFor }) => (
    <DeltaCell
      data-testid="score-delta"
      data-direction={scoreDeltaDirection(row.scoreDelta)}
      title={deltaTitleFor(row)}
    >
      <Body5 color={deltaColor(row.scoreDelta)}>{formatScoreDelta(row.scoreDelta)}</Body5>
    </DeltaCell>
  ),

  // The company's whole series, in both grains — see MovementCell for which mark each one draws and
  // why the line never narrows with a filter.
  movement: (row, { movement, onOpenHistory }) => (
    <div>
      <MovementCell
        entry={movementEntry(movement, row)}
        row={row}
        domains={movement.domains}
        aggregated={!!row.aggregated}
        onOpenHistory={onOpenHistory}
      />
    </div>
  ),

  // The column drops seconds for readability; the tooltip keeps the exact instant — and, on an
  // aggregated row, the span the average was taken over.
  knowableAt: (row, { knowableAtTitleFor, occurrenceNote }) => (
    <NumericCell title={knowableAtTitleFor(row)}>
      <Body7>
        {formatKnowableAt(row.knowableAt)}
        {occurrenceNote(row)}
      </Body7>
    </NumericCell>
  ),
};
